(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined'
    ? factory(exports, require('@egjs/flicking'))
    : typeof define === 'function' && define.amd
      ? define(['exports', '@egjs/flicking'], factory)
      : ((global = typeof globalThis !== 'undefined' ? globalThis : global || self),
        factory(((global.Flicking = global.Flicking || {}), (global.Flicking.Plugins = {})), global.Flicking));
})(this, function (exports2, flicking) {
  'use strict';
  const ARROW = {
    PREV_SELECTOR: '.flicking-arrow-prev',
    NEXT_SELECTOR: '.flicking-arrow-next',
    DISABLED_CLASS: 'flicking-arrow-disabled',
  };
  const PAGINATION = {
    SELECTOR: '.flicking-pagination',
    PREFIX: 'flicking-pagination',
    BULLET_WRAPPER_SUFFIX: 'bullets',
    BULLET_SUFFIX: 'bullet',
    BULLET_ACTIVE_SUFFIX: 'bullet-active',
    FRACTION_WRAPPER_SUFFIX: 'fraction',
    FRACTION_CURRENT_SUFFIX: 'fraction-current',
    FRACTION_TOTAL_SUFFIX: 'fraction-total',
    SCROLL_UNINIT_SUFFIX: 'uninitialized',
    SCROLL_WRAPPER_SUFFIX: 'scroll',
    SCROLL_SLIDER_SUFFIX: 'slider',
    SCROLL_PREV_SUFFIX: 'bullet-prev',
    SCROLL_NEXT_SUFFIX: 'bullet-next',
    TYPE: {
      BULLET: 'bullet',
      FRACTION: 'fraction',
      SCROLL: 'scroll',
    },
  };
  const SYNC = {
    TYPE: {
      CAMERA: 'camera',
      INDEX: 'index',
    },
  };
  const BROWSER = {
    CLICK: 'click',
    MOUSE_DOWN: 'mousedown',
    TOUCH_START: 'touchstart',
  };
  const addClass = (el, className) => {
    if (!el) return;
    if (el.classList) {
      el.classList.add(className);
    } else {
      const classes = el.className.split(' ');
      if (classes.indexOf(className) < 0) {
        el.className = `${el.className} ${className}`;
      }
    }
  };
  const removeClass = (el, className) => {
    if (!el) return;
    if (el.classList) {
      el.classList.remove(className);
    } else {
      const classRegex = new RegExp(`( |^)${className}( |$)`, 'g');
      el.className.replace(classRegex, ' ');
    }
  };
  const getElement = (selector, parent, pluginName) => {
    const el = parent.querySelector(selector);
    if (!el) {
      throw new Error(`[Flicking-${pluginName}] Couldn't find element with the given selector: ${selector}`);
    }
    return el;
  };
  class Arrow {
    /**
     * @param options - Options for the Arrow instance
     * @example
     * ```ts
     * flicking.addPlugins(new Arrow({ parentEl: null, prevElSelector: ".flicking-arrow-prev", nextElSelector: ".flicking-arrow-next" }));
     * ```
     */
    constructor(options = {}) {
      this._flicking = null;
      this._preventInputPropagation = (e) => {
        e.stopPropagation();
      };
      this._onPrevClick = () => {
        const flicking2 = this._flicking;
        const camera = flicking2.camera;
        const anchorPoints = camera.anchorPoints;
        if (flicking2.animating || anchorPoints.length <= 0) return;
        const firstAnchor = anchorPoints[0];
        const moveCount2 = this._moveCount;
        if (this._moveByViewportSize) {
          flicking2.control.moveToPosition(camera.position - camera.size, flicking2.duration).catch(this._onCatch);
        } else {
          if (flicking2.circularEnabled) {
            let targetPanel = flicking2.currentPanel;
            for (let i = 0; i < moveCount2; i++) {
              targetPanel = targetPanel.prev();
            }
            targetPanel.focus().catch(this._onCatch);
          } else if (flicking2.index > firstAnchor.panel.index) {
            flicking2.moveTo(Math.max(flicking2.index - moveCount2, firstAnchor.panel.index)).catch(this._onCatch);
          } else if (camera.position > camera.range.min) {
            flicking2.moveTo(flicking2.index).catch(this._onCatch);
          }
        }
      };
      this._onNextClick = () => {
        const flicking2 = this._flicking;
        const camera = flicking2.camera;
        const anchorPoints = camera.anchorPoints;
        if (flicking2.animating || anchorPoints.length <= 0) return;
        const lastAnchor = anchorPoints[anchorPoints.length - 1];
        const moveCount2 = this._moveCount;
        if (this._moveByViewportSize) {
          flicking2.control.moveToPosition(camera.position + camera.size, flicking2.duration).catch(this._onCatch);
        } else {
          if (flicking2.circularEnabled) {
            let targetPanel = flicking2.currentPanel;
            for (let i = 0; i < moveCount2; i++) {
              targetPanel = targetPanel.next();
            }
            targetPanel.focus().catch(this._onCatch);
          } else if (flicking2.index < lastAnchor.panel.index) {
            flicking2.moveTo(Math.min(flicking2.index + moveCount2, lastAnchor.panel.index)).catch(this._onCatch);
          } else if (camera.position > camera.range.min) {
            flicking2.moveTo(flicking2.index).catch(this._onCatch);
          }
        }
      };
      this._onAnimation = () => {
        const flicking2 = this._flicking;
        const camera = flicking2.camera;
        const controller = flicking2.control.controller;
        if (flicking2.holding) {
          this._updateClass(camera.position);
        } else {
          this._updateClass(controller.animatingContext.end);
        }
      };
      this._onCatch = (err) => {
        if (err instanceof flicking.FlickingError) return;
        throw err;
      };
      const {
        parentEl = null,
        prevElSelector = ARROW.PREV_SELECTOR,
        nextElSelector = ARROW.NEXT_SELECTOR,
        disabledClass = ARROW.DISABLED_CLASS,
        moveCount = 1,
        moveByViewportSize = false,
      } = options;
      this._parentEl = parentEl;
      this._prevElSelector = prevElSelector;
      this._nextElSelector = nextElSelector;
      this._disabledClass = disabledClass;
      this._moveCount = moveCount;
      this._moveByViewportSize = moveByViewportSize;
    }
    /** The "previous" arrow HTMLElement
     * @readonly
     */
    get prevEl() {
      return this._prevEl;
    }
    /** The "next" arrow HTMLElement
     * @readonly
     */
    get nextEl() {
      return this._nextEl;
    }
    /** Current value of the {@link ArrowOptions.parentEl | parentEl} option. */
    get parentEl() {
      return this._parentEl;
    }
    /** Current value of the {@link ArrowOptions.prevElSelector | prevElSelector} option. */
    get prevElSelector() {
      return this._prevElSelector;
    }
    /** Current value of the {@link ArrowOptions.nextElSelector | nextElSelector} option. */
    get nextElSelector() {
      return this._nextElSelector;
    }
    /** Current value of the {@link ArrowOptions.disabledClass | disabledClass} option. */
    get disabledClass() {
      return this._disabledClass;
    }
    /** Current value of the {@link ArrowOptions.moveCount | moveCount} option. */
    get moveCount() {
      return this._moveCount;
    }
    /** Current value of the {@link ArrowOptions.moveByViewportSize | moveByViewportSize} option. */
    get moveByViewportSize() {
      return this._moveByViewportSize;
    }
    /** Sets {@link ArrowOptions.parentEl | parentEl}. */
    set parentEl(val) {
      this._parentEl = val;
    }
    /** Sets {@link ArrowOptions.prevElSelector | prevElSelector}. */
    set prevElSelector(val) {
      this._prevElSelector = val;
    }
    /** Sets {@link ArrowOptions.nextElSelector | nextElSelector}. */
    set nextElSelector(val) {
      this._nextElSelector = val;
    }
    /** Sets {@link ArrowOptions.disabledClass | disabledClass}. */
    set disabledClass(val) {
      this._disabledClass = val;
    }
    /** Sets {@link ArrowOptions.moveCount | moveCount}. */
    set moveCount(val) {
      this._moveCount = val;
    }
    /** Sets {@link ArrowOptions.moveByViewportSize | moveByViewportSize}. */
    set moveByViewportSize(val) {
      this._moveByViewportSize = val;
    }
    /** Initialize the plugin and attach arrow event listeners to the Flicking instance.
     * @param flicking - The Flicking instance to attach this plugin to
     */
    init(flicking$1) {
      if (this._flicking) {
        this.destroy();
      }
      this._flicking = flicking$1;
      flicking$1.on(flicking.EVENTS.MOVE, this._onAnimation);
      const parentEl = this._parentEl ? this._parentEl : flicking$1.element;
      const prevEl = getElement(this._prevElSelector, parentEl, 'Arrow');
      const nextEl = getElement(this._nextElSelector, parentEl, 'Arrow');
      [BROWSER.MOUSE_DOWN, BROWSER.TOUCH_START].forEach((evt) => {
        prevEl.addEventListener(evt, this._preventInputPropagation, { passive: true });
        nextEl.addEventListener(evt, this._preventInputPropagation, { passive: true });
      });
      prevEl.addEventListener(BROWSER.CLICK, this._onPrevClick);
      nextEl.addEventListener(BROWSER.CLICK, this._onNextClick);
      this._prevEl = prevEl;
      this._nextEl = nextEl;
      this.update();
    }
    /** Destroy the plugin and remove all arrow event listeners. */
    destroy() {
      const flicking$1 = this._flicking;
      if (!flicking$1) {
        return;
      }
      flicking$1.off(flicking.EVENTS.MOVE, this._onAnimation);
      const prevEl = this._prevEl;
      const nextEl = this._nextEl;
      [BROWSER.MOUSE_DOWN, BROWSER.TOUCH_START].forEach((evt) => {
        prevEl.removeEventListener(evt, this._preventInputPropagation);
        nextEl.removeEventListener(evt, this._preventInputPropagation);
      });
      prevEl.removeEventListener(BROWSER.CLICK, this._onPrevClick);
      nextEl.removeEventListener(BROWSER.CLICK, this._onNextClick);
      this._flicking = null;
    }
    /** Update the arrow disabled/enabled state based on the current camera position. */
    update() {
      this._updateClass(this._flicking.camera.position);
    }
    _updateClass(pos) {
      const flicking2 = this._flicking;
      const disabledClass = this._disabledClass;
      const prevEl = this._prevEl;
      const nextEl = this._nextEl;
      const cameraRange = flicking2.camera.range;
      const stopAtPrevEdge = flicking2.circularEnabled ? false : pos <= cameraRange.min;
      const stopAtNextEdge = flicking2.circularEnabled ? false : pos >= cameraRange.max;
      if (stopAtPrevEdge) {
        addClass(prevEl, disabledClass);
      } else {
        removeClass(prevEl, disabledClass);
      }
      if (stopAtNextEdge) {
        addClass(nextEl, disabledClass);
      } else {
        removeClass(nextEl, disabledClass);
      }
    }
  }
  class AutoPlay {
    /**
     * @param options - Options for the AutoPlay instance
     * @example
     * ```ts
     * flicking.addPlugins(new AutoPlay({ duration: 2000, direction: "NEXT" }));
     * ```
     */
    constructor(options = {}) {
      this._flicking = null;
      this._timerId = 0;
      this._mouseEntered = false;
      this._playing = false;
      this.play = () => {
        this._movePanel(this._duration);
      };
      this.stop = () => {
        this._playing = false;
        clearTimeout(this._timerId);
      };
      this._onMouseEnter = () => {
        this._mouseEntered = true;
        this.stop();
      };
      this._onMouseLeave = () => {
        this._mouseEntered = false;
        this._movePanel(this._delayAfterHover);
      };
      const {
        duration = 2e3,
        animationDuration = void 0,
        direction = flicking.DIRECTION.NEXT,
        stopOnHover = false,
        stopOnInit = false,
        delayAfterHover,
      } = options;
      this._duration = duration;
      this._animationDuration = animationDuration;
      this._direction = direction;
      this._stopOnHover = stopOnHover;
      this._stopOnInit = stopOnInit;
      this._delayAfterHover = delayAfterHover != null ? delayAfterHover : duration;
    }
    /** Current value of the {@link AutoPlayOptions.duration | duration} option. */
    get duration() {
      return this._duration;
    }
    /** Current value of the {@link AutoPlayOptions.animationDuration | animationDuration} option. */
    get animationDuration() {
      return this._animationDuration;
    }
    /** Current value of the {@link AutoPlayOptions.direction | direction} option. */
    get direction() {
      return this._direction;
    }
    /** Current value of the {@link AutoPlayOptions.stopOnHover | stopOnHover} option. */
    get stopOnHover() {
      return this._stopOnHover;
    }
    /** Current value of the {@link AutoPlayOptions.stopOnInit | stopOnInit} option. */
    get stopOnInit() {
      return this._stopOnInit;
    }
    /** Current value of the {@link AutoPlayOptions.delayAfterHover | delayAfterHover} option. */
    get delayAfterHover() {
      return this._delayAfterHover;
    }
    /** Whether the autoplay is currently active
     * @readonly
     */
    get playing() {
      return this._playing;
    }
    /** Sets {@link AutoPlayOptions.duration | duration}. */
    set duration(val) {
      this._duration = val;
    }
    /** Sets {@link AutoPlayOptions.animationDuration | animationDuration}. */
    set animationDuration(val) {
      this._animationDuration = val;
    }
    /** Sets {@link AutoPlayOptions.direction | direction}. */
    set direction(val) {
      this._direction = val;
    }
    /** Sets {@link AutoPlayOptions.stopOnHover | stopOnHover}. */
    set stopOnHover(val) {
      this._stopOnHover = val;
    }
    /** Sets {@link AutoPlayOptions.stopOnInit | stopOnInit}. */
    set stopOnInit(val) {
      this._stopOnInit = val;
    }
    /** Sets {@link AutoPlayOptions.delayAfterHover | delayAfterHover}. */
    set delayAfterHover(val) {
      this._delayAfterHover = val;
    }
    /** Initialize the plugin and start autoplay on the given Flicking instance.
     * @param flicking - The Flicking instance to attach this plugin to
     */
    init(flicking$1) {
      if (this._flicking) {
        this.destroy();
      }
      flicking$1.on({
        [flicking.EVENTS.MOVE_START]: this.stop,
        [flicking.EVENTS.HOLD_START]: this.stop,
        [flicking.EVENTS.MOVE_END]: this.play,
        [flicking.EVENTS.SELECT]: this.play,
      });
      this._flicking = flicking$1;
      if (this._stopOnHover) {
        const targetEl = this._flicking.element;
        targetEl.addEventListener('mouseenter', this._onMouseEnter, false);
        targetEl.addEventListener('mouseleave', this._onMouseLeave, false);
      }
      if (!this._stopOnInit) {
        this.play();
      }
    }
    /** Destroy the plugin, stop autoplay, and remove all event listeners. */
    destroy() {
      const flicking$1 = this._flicking;
      this._mouseEntered = false;
      this.stop();
      if (!flicking$1) {
        return;
      }
      flicking$1.off(flicking.EVENTS.MOVE_START, this.stop);
      flicking$1.off(flicking.EVENTS.HOLD_START, this.stop);
      flicking$1.off(flicking.EVENTS.MOVE_END, this.play);
      flicking$1.off(flicking.EVENTS.SELECT, this.play);
      const targetEl = flicking$1.element;
      targetEl.removeEventListener('mouseenter', this._onMouseEnter, false);
      targetEl.removeEventListener('mouseleave', this._onMouseLeave, false);
      this._flicking = null;
    }
    /** Update the plugin state. This is a no-op for AutoPlay. */
    update() {}
    _movePanel(duration) {
      const flicking$1 = this._flicking;
      const direction = this._direction;
      if (!flicking$1) {
        return;
      }
      this.stop();
      if (this._mouseEntered || flicking$1.animating) {
        return;
      }
      this._playing = true;
      this._timerId = window.setTimeout(() => {
        var _a, _b;
        let animationDuration = this._animationDuration || flicking$1.duration;
        const moveType = flicking$1.moveType;
        if (
          moveType === flicking.MOVE_TYPE.FREE_SCROLL ||
          (moveType == null ? void 0 : moveType[0]) === flicking.MOVE_TYPE.FREE_SCROLL
        ) {
          const range = flicking$1.camera.range;
          const cameraPosition = flicking$1.camera.position;
          const currentPanel = flicking$1.currentPanel;
          const prevPanel = currentPanel.prev();
          const nextPanel = currentPanel.next();
          const currentPosition = currentPanel.position;
          let nextPosition = (_a = nextPanel == null ? void 0 : nextPanel.position) != null ? _a : range.max;
          let prevPosition = (_b = prevPanel == null ? void 0 : prevPanel.position) != null ? _b : range.min;
          if (prevPosition > currentPosition) {
            prevPosition = range.min - (range.max - prevPosition);
          }
          if (nextPosition < currentPosition) {
            nextPosition += range.max;
          }
          if (direction === flicking.DIRECTION.NEXT) {
            const size = nextPosition - currentPosition;
            let restSize = nextPosition - cameraPosition;
            if (cameraPosition < currentPosition) {
              restSize = nextPosition - cameraPosition;
            }
            animationDuration *= restSize / size;
          } else {
            const size = currentPosition - prevPosition;
            let restSize = cameraPosition - prevPosition;
            if (cameraPosition > currentPosition) {
              restSize = cameraPosition - prevPosition;
            }
            animationDuration *= restSize / size;
          }
        }
        if (direction === flicking.DIRECTION.NEXT) {
          flicking$1.next(animationDuration).catch(() => void 0);
        } else {
          flicking$1.prev(animationDuration).catch(() => void 0);
        }
        this.play();
      }, duration);
    }
  }
  class Fade {
    /**
     * @param selector - CSS selector for the element to apply the fade effect. If empty, the panel element itself is used
     * @param scale - Effect amplification scale
     * @example
     * ```ts
     * flicking.addPlugins(new Fade("p", 1));
     * ```
     */
    constructor(selector = '', scale = 1) {
      this.update = () => {
        this._onMove();
      };
      this._onMove = () => {
        const flicking2 = this._flicking;
        const selector2 = this._selector;
        const scale2 = this._scale;
        if (!flicking2) return;
        const panels = flicking2.visiblePanels;
        panels.forEach((panel) => {
          const progress = panel.outsetProgress;
          const el = panel.element;
          const target = selector2 ? el.querySelector(selector2) : el;
          if (target) {
            const opacity = Math.min(1, Math.max(0, 1 - Math.abs(progress * scale2)));
            target.style.opacity = `${opacity}`;
          }
        });
      };
      this._flicking = null;
      this._selector = selector;
      this._scale = scale;
    }
    /** CSS selector for the element to apply the fade effect. If empty, the panel element itself is used
     * @readonly
     */
    get selector() {
      return this._selector;
    }
    /** Effect amplification scale
     * @readonly
     */
    get scale() {
      return this._scale;
    }
    /** Sets the CSS selector for the target fade element. */
    set selector(val) {
      this._selector = val;
    }
    /** Sets the effect amplification scale. */
    set scale(val) {
      this._scale = val;
    }
    /** Initialize the plugin and apply the fade effect to the Flicking instance.
     * @param flicking - The Flicking instance to attach this plugin to
     */
    init(flicking$1) {
      if (this._flicking) {
        this.destroy();
      }
      this._flicking = flicking$1;
      flicking$1.on(flicking.EVENTS.MOVE, this._onMove);
      flicking$1.on(flicking.EVENTS.AFTER_RESIZE, this.update);
      this._onMove();
    }
    /** Destroy the plugin and remove all event listeners. */
    destroy() {
      if (!this._flicking) return;
      this._flicking.off(flicking.EVENTS.MOVE, this._onMove);
      this._flicking.off(flicking.EVENTS.AFTER_RESIZE, this.update);
      this._flicking = null;
    }
  }
  class Parallax {
    /**
     * @param selector - CSS selector for the element to apply the parallax effect. If empty, the panel element itself is used
     * @param scale - Effect amplification scale
     * @example
     * ```ts
     * flicking.addPlugins(new Parallax("img", 1));
     * ```
     */
    constructor(selector = '', scale = 1) {
      this.update = () => {
        this._onMove();
      };
      this._onMove = () => {
        const flicking2 = this._flicking;
        if (!flicking2) return;
        const panels = flicking2.visiblePanels;
        panels.forEach((panel) => {
          const progress = panel.outsetProgress;
          const el = panel.element;
          const target = this._selector ? el.querySelector(this._selector) : el;
          const parentTarget = target.parentNode;
          const rect = target.getBoundingClientRect();
          const parentRect = parentTarget.getBoundingClientRect();
          const position = ((parentRect.width - rect.width) / 2) * progress * this._scale;
          const transform = `translate(-50%) translate(${position}px)`;
          const style = target.style;
          style.cssText += `transform: ${transform};-webkit-transform: ${transform};-ms-transform:${transform}`;
        });
      };
      this._flicking = null;
      this._selector = selector;
      this._scale = scale;
    }
    /** CSS selector for the element to apply the parallax effect. If empty, the panel element itself is used
     * @readonly
     */
    get selector() {
      return this._selector;
    }
    /** Effect amplification scale
     * @readonly
     */
    get scale() {
      return this._scale;
    }
    /** Sets the CSS selector for the target parallax element. */
    set selector(val) {
      this._selector = val;
    }
    /** Sets the effect amplification scale. */
    set scale(val) {
      this._scale = val;
    }
    /** Initialize the plugin and apply the parallax effect to the Flicking instance.
     * @param flicking - The Flicking instance to attach this plugin to
     */
    init(flicking$1) {
      if (this._flicking) {
        this.destroy();
      }
      this._flicking = flicking$1;
      flicking$1.on(flicking.EVENTS.MOVE, this._onMove);
      flicking$1.on(flicking.EVENTS.AFTER_RESIZE, this.update);
      this._onMove();
    }
    /** Destroy the plugin and remove all event listeners. */
    destroy() {
      if (!this._flicking) return;
      this._flicking.off(flicking.EVENTS.MOVE, this._onMove);
      this._flicking.off(flicking.EVENTS.AFTER_RESIZE, this.update);
      this._flicking = null;
    }
  }
  class Perspective {
    /**
     * @param options - Options for the Perspective instance
     * @example
     * ```ts
     * flicking.addPlugins(new Perspective({ selector: "p", scale: 1, rotate: 1, perspective: 1000 }));
     * ```
     */
    constructor(options = {}) {
      this.update = () => {
        this._onMove();
      };
      this._onMove = () => {
        const flicking2 = this._flicking;
        const selector2 = this._selector;
        const scale2 = this._scale;
        const rotate2 = this._rotate;
        const perspective2 = this._perspective;
        if (!flicking2) return;
        const horizontal = flicking2.horizontal;
        const panels = flicking2.visiblePanels;
        panels.forEach((panel) => {
          const progress = panel.outsetProgress;
          const el = panel.element;
          const target = selector2 ? el.querySelector(selector2) : el;
          const panelScale = 1 / (Math.abs(progress * scale2) + 1);
          const rotateDegree =
            progress > 0 ? Math.min(90, progress * 100 * rotate2) : Math.max(-90, progress * 100 * rotate2);
          const [rotateX, rotateY] = horizontal ? [0, rotateDegree] : [rotateDegree, 0];
          target.style.transform = `perspective(${perspective2}px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(${panelScale})`;
        });
      };
      const { selector = '', scale = 1, rotate = 1, perspective = 1e3 } = options;
      this._flicking = null;
      this._selector = selector;
      this._scale = scale;
      this._rotate = rotate;
      this._perspective = perspective;
    }
    /** Current value of the {@link PerspectiveOptions.selector | selector} option. */
    get selector() {
      return this._selector;
    }
    /** Current value of the {@link PerspectiveOptions.scale | scale} option. */
    get scale() {
      return this._scale;
    }
    /** Current value of the {@link PerspectiveOptions.rotate | rotate} option. */
    get rotate() {
      return this._rotate;
    }
    /** Current value of the {@link PerspectiveOptions.perspective | perspective} option. */
    get perspective() {
      return this._perspective;
    }
    /** Sets {@link PerspectiveOptions.selector | selector}. */
    set selector(val) {
      this._selector = val;
    }
    /** Sets {@link PerspectiveOptions.scale | scale}. */
    set scale(val) {
      this._scale = val;
    }
    /** Sets {@link PerspectiveOptions.rotate | rotate}. */
    set rotate(val) {
      this._rotate = val;
    }
    /** Sets {@link PerspectiveOptions.perspective | perspective}. */
    set perspective(val) {
      this._perspective = val;
    }
    /** Initialize the plugin and apply the perspective effect to the Flicking instance.
     * @param flicking - The Flicking instance to attach this plugin to
     */
    init(flicking$1) {
      if (this._flicking) {
        this.destroy();
      }
      this._flicking = flicking$1;
      flicking$1.on(flicking.EVENTS.MOVE, this._onMove);
      flicking$1.on(flicking.EVENTS.AFTER_RESIZE, this.update);
      this._onMove();
    }
    /** Destroy the plugin and remove all event listeners. */
    destroy() {
      if (!this._flicking) return;
      this._flicking.off(flicking.EVENTS.MOVE, this._onMove);
      this._flicking.off(flicking.EVENTS.AFTER_RESIZE, this.update);
      this._flicking = null;
    }
  }
  class Sync {
    /**
     * @param options - Options for the Sync instance
     * @example
     * ```ts
     * flicking.addPlugins(new Sync({
     *   type: "camera",
     *   synchronizedFlickingOptions: [
     *     { flicking: flicking1 },
     *     { flicking: flicking2, isClickable: true }
     *   ]
     * }));
     * ```
     */
    constructor(options = {}) {
      this._flicking = null;
      this._disabledIndex = [];
      this._addEvents = () => {
        const type2 = this._type;
        const synced = this._synchronizedFlickingOptions;
        synced.forEach(({ flicking: flicking$1, isSlidable, isClickable }) => {
          if (type2 === SYNC.TYPE.CAMERA) {
            flicking$1.on(flicking.EVENTS.MOVE, this._onMove);
            flicking$1.on(flicking.EVENTS.MOVE_START, this._onMoveStart);
            flicking$1.on(flicking.EVENTS.MOVE_END, this._onMoveEnd);
          }
          if (type2 === SYNC.TYPE.INDEX && isSlidable) {
            flicking$1.on(flicking.EVENTS.WILL_CHANGE, this._onIndexChange);
            flicking$1.on(flicking.EVENTS.WILL_RESTORE, this._onIndexChange);
          }
          if (isClickable) {
            flicking$1.on(flicking.EVENTS.SELECT, this._onSelect);
          }
        });
      };
      this._removeEvents = () => {
        const type2 = this._type;
        const synced = this._synchronizedFlickingOptions;
        synced.forEach(({ flicking: flicking$1, isSlidable, isClickable }) => {
          if (type2 === SYNC.TYPE.CAMERA) {
            flicking$1.off(flicking.EVENTS.MOVE, this._onMove);
            flicking$1.off(flicking.EVENTS.MOVE_START, this._onMoveStart);
            flicking$1.off(flicking.EVENTS.MOVE_END, this._onMoveEnd);
          }
          if (type2 === SYNC.TYPE.INDEX && isSlidable) {
            flicking$1.off(flicking.EVENTS.WILL_CHANGE, this._onIndexChange);
            flicking$1.off(flicking.EVENTS.WILL_RESTORE, this._onIndexChange);
          }
          if (isClickable) {
            flicking$1.off(flicking.EVENTS.SELECT, this._onSelect);
          }
        });
      };
      this._onIndexChange = (e) => {
        const flicking2 = e.currentTarget;
        if (!flicking2.initialized) {
          return;
        }
        this._synchronizeByIndex(flicking2, e.index);
      };
      this._onMove = (e) => {
        const camera = e.currentTarget.camera;
        const progress = (camera.position - camera.range.min) / camera.rangeDiff;
        this._synchronizedFlickingOptions.forEach(({ flicking: flicking2 }) => {
          if (flicking2 === e.currentTarget) return;
          let targetPosition = 0;
          if (camera.position < camera.range.min) {
            targetPosition = camera.position;
          } else if (camera.position > camera.range.max) {
            targetPosition = flicking2.camera.range.max + camera.position - camera.range.max;
          } else {
            targetPosition = flicking2.camera.range.min + flicking2.camera.rangeDiff * progress;
          }
          void flicking2.camera.lookAt(targetPosition);
        });
      };
      this._onMoveStart = (e) => {
        this._disabledIndex = [];
        this._synchronizedFlickingOptions.forEach(({ flicking: flicking2 }, i) => {
          if (flicking2 !== e.currentTarget && flicking2.control.controller.enabled) {
            this._disabledIndex.push(i);
            flicking2.disableInput();
          }
        });
      };
      this._onMoveEnd = (e) => {
        this._disabledIndex.forEach((i) => {
          const flicking2 = this._synchronizedFlickingOptions[i].flicking;
          flicking2.enableInput();
          flicking2.control.updateInput();
        });
      };
      this._onSelect = (e) => {
        void e.currentTarget.moveTo(e.index).catch(() => void 0);
        this._synchronizeByIndex(e.currentTarget, e.index);
      };
      this._synchronizeByIndex = (activeFlicking, index) => {
        const synchronizedFlickingOptions2 = this._synchronizedFlickingOptions;
        this._preventEvent(() => {
          synchronizedFlickingOptions2.forEach((options2) => {
            this._updateClass(options2, index);
            const { flicking: flicking$1 } = options2;
            if (flicking$1 === activeFlicking) return;
            const targetIndex = flicking.clamp(index, 0, flicking$1.panels.length);
            if (flicking$1.animating) {
              flicking$1.once(flicking.EVENTS.MOVE_END, () => {
                void flicking$1.moveTo(targetIndex).catch(() => void 0);
              });
            } else {
              void flicking$1.moveTo(targetIndex);
            }
          });
        });
      };
      this._updateClass = ({ flicking: flicking2, activeClass }, index) => {
        if (!activeClass) return;
        flicking2.panels.forEach((panel) => {
          if (panel.index === index) {
            addClass(panel.element, activeClass);
          } else {
            removeClass(panel.element, activeClass);
          }
        });
      };
      const { type = SYNC.TYPE.CAMERA, synchronizedFlickingOptions = [] } = options;
      this._type = type;
      this._synchronizedFlickingOptions = synchronizedFlickingOptions;
    }
    /** Current value of the {@link SyncOptions.type | type} option. */
    get type() {
      return this._type;
    }
    /** Current value of the {@link SyncOptions.synchronizedFlickingOptions | synchronizedFlickingOptions} option. */
    get synchronizedFlickingOptions() {
      return this._synchronizedFlickingOptions;
    }
    /** Sets {@link SyncOptions.type | type}. */
    set type(val) {
      this._type = val;
    }
    /** Sets {@link SyncOptions.synchronizedFlickingOptions | synchronizedFlickingOptions}. */
    set synchronizedFlickingOptions(val) {
      this._synchronizedFlickingOptions = val;
    }
    /** Initialize the plugin and set up synchronization event listeners between Flicking instances.
     * @param flicking - The Flicking instance to attach this plugin to
     */
    init(flicking2) {
      const synced = this._synchronizedFlickingOptions;
      if (this._flicking) {
        this.destroy();
      }
      this._flicking = flicking2;
      this._addEvents();
      synced.forEach((options) => {
        const { flicking: syncedFlicking } = options;
        this._updateClass(options, syncedFlicking.defaultIndex);
      });
    }
    /** Destroy the plugin and remove all synchronization event listeners. */
    destroy() {
      const flicking2 = this._flicking;
      if (!flicking2) {
        return;
      }
      this._removeEvents();
      this._flicking = null;
    }
    /** Update the active class state for all synchronized Flicking instances. */
    update() {
      this._synchronizedFlickingOptions.forEach((options) => {
        this._updateClass(options, options.flicking.index);
      });
    }
    _preventEvent(fn) {
      this._removeEvents();
      fn();
      this._addEvents();
    }
  }
  class Renderer {
    constructor(options) {
      const { flicking: flicking2, pagination, wrapper } = options;
      this._flicking = flicking2;
      this._pagination = pagination;
      this._wrapper = wrapper;
    }
    _createBulletFromString(html, index) {
      const range = document.createRange();
      const frag = range.createContextualFragment(html);
      const bullet = frag.firstChild;
      this._addBulletEvents(bullet, index);
      return bullet;
    }
    _addBulletEvents(bullet, index) {
      const anchorPoints = this._flicking.camera.anchorPoints;
      const panelIndex = anchorPoints[index].panel.index;
      bullet.addEventListener(BROWSER.MOUSE_DOWN, (e) => {
        e.stopPropagation();
      });
      bullet.addEventListener(
        BROWSER.TOUCH_START,
        (e) => {
          e.stopPropagation();
        },
        { passive: true },
      );
      bullet.addEventListener(BROWSER.CLICK, () => {
        this._flicking.moveTo(panelIndex).catch((err) => {
          if (err instanceof flicking.FlickingError) return;
          throw err;
        });
      });
    }
  }
  class BulletRenderer extends Renderer {
    constructor() {
      super(...arguments);
      this._bullets = [];
      this._prevIndex = -1;
    }
    get _bulletClass() {
      const pagination = this._pagination;
      return `${pagination.classPrefix}-${PAGINATION.BULLET_SUFFIX}`;
    }
    get _activeClass() {
      const pagination = this._pagination;
      return `${pagination.classPrefix}-${PAGINATION.BULLET_ACTIVE_SUFFIX}`;
    }
    destroy() {
      this._bullets = [];
      this._prevIndex = -1;
    }
    render() {
      const flicking2 = this._flicking;
      const pagination = this._pagination;
      const wrapper = this._wrapper;
      const bulletClass = this._bulletClass;
      const activeClass = this._activeClass;
      const renderBullet = pagination.renderBullet;
      const renderActiveBullet = pagination.renderActiveBullet;
      const bulletWrapperClass = `${pagination.classPrefix}-${PAGINATION.BULLET_WRAPPER_SUFFIX}`;
      const anchorPoints = flicking2.camera.anchorPoints;
      addClass(wrapper, bulletWrapperClass);
      wrapper.innerHTML = anchorPoints
        .map((anchorPoint, index) => {
          if (renderActiveBullet && anchorPoint.panel.index === flicking2.index) {
            return renderActiveBullet(bulletClass, index);
          } else {
            return renderBullet(bulletClass, index);
          }
        })
        .join('\n');
      const bullets = [].slice.call(wrapper.children);
      bullets.forEach((bullet, index) => {
        const anchorPoint = anchorPoints[index];
        if (anchorPoint.panel.index === flicking2.index) {
          addClass(bullet, activeClass);
          this._prevIndex = index;
        }
        this._addBulletEvents(bullet, index);
      });
      this._bullets = bullets;
    }
    update(index) {
      const flicking2 = this._flicking;
      const pagination = this._pagination;
      const wrapper = this._wrapper;
      const bullets = this._bullets;
      const bulletClass = this._bulletClass;
      const activeClass = this._activeClass;
      const prevIndex = this._prevIndex;
      const anchorPoints = flicking2.camera.anchorPoints;
      const renderBullet = pagination.renderBullet;
      const renderActiveBullet = pagination.renderActiveBullet;
      if (anchorPoints.length <= 0) return;
      const anchorOffset = anchorPoints[0].panel.index;
      const activeBulletIndex = index - anchorOffset;
      if (prevIndex === activeBulletIndex) return;
      if (renderActiveBullet) {
        const prevBullet = bullets[prevIndex];
        if (prevBullet) {
          const newBullet = this._createBulletFromString(renderBullet(bulletClass, prevIndex), prevIndex);
          prevBullet.parentElement.replaceChild(newBullet, prevBullet);
          bullets[prevIndex] = newBullet;
        }
        const activeBullet = bullets[activeBulletIndex];
        const newActiveBullet = this._createBulletFromString(
          renderActiveBullet(`${bulletClass} ${activeClass}`, activeBulletIndex),
          activeBulletIndex,
        );
        wrapper.replaceChild(newActiveBullet, activeBullet);
        bullets[activeBulletIndex] = newActiveBullet;
      } else {
        const activeBullet = bullets[activeBulletIndex];
        const prevBullet = bullets[prevIndex];
        if (prevBullet) {
          removeClass(prevBullet, activeClass);
        }
        addClass(activeBullet, activeClass);
      }
      this._prevIndex = activeBulletIndex;
    }
  }
  class FractionRenderer extends Renderer {
    constructor() {
      super(...arguments);
      this._prevIndex = -1;
      this._prevTotal = -1;
    }
    destroy() {
      this._prevIndex = -1;
      this._prevTotal = -1;
    }
    render() {
      const flicking2 = this._flicking;
      const wrapper = this._wrapper;
      const pagination = this._pagination;
      const fractionWrapperClass = `${pagination.classPrefix}-${PAGINATION.FRACTION_WRAPPER_SUFFIX}`;
      const fractionCurrentClass = `${pagination.classPrefix}-${PAGINATION.FRACTION_CURRENT_SUFFIX}`;
      const fractionTotalClass = `${pagination.classPrefix}-${PAGINATION.FRACTION_TOTAL_SUFFIX}`;
      addClass(wrapper, fractionWrapperClass);
      wrapper.innerHTML = pagination.renderFraction(fractionCurrentClass, fractionTotalClass);
      this.update(flicking2.index);
    }
    update(index) {
      const flicking2 = this._flicking;
      const wrapper = this._wrapper;
      const pagination = this._pagination;
      const anchorPoints = flicking2.camera.anchorPoints;
      const currentIndex = anchorPoints.length > 0 ? index - anchorPoints[0].panel.index + 1 : 0;
      const anchorCount = anchorPoints.length;
      if (currentIndex === this._prevIndex && anchorCount === this._prevTotal) return;
      const fractionCurrentSelector = `.${pagination.classPrefix}-${PAGINATION.FRACTION_CURRENT_SUFFIX}`;
      const fractionTotalSelector = `.${pagination.classPrefix}-${PAGINATION.FRACTION_TOTAL_SUFFIX}`;
      const currentWrapper = wrapper.querySelector(fractionCurrentSelector);
      const totalWrapper = wrapper.querySelector(fractionTotalSelector);
      currentWrapper.innerHTML = pagination.fractionCurrentFormat(currentIndex);
      totalWrapper.innerHTML = pagination.fractionTotalFormat(anchorCount);
      this._prevIndex = currentIndex;
      this._prevTotal = anchorCount;
    }
  }
  class ScrollBulletRenderer extends Renderer {
    constructor() {
      super(...arguments);
      this._bullets = [];
      this._bulletSize = 0;
      this._previousIndex = -1;
      this._sliderIndex = -1;
      this.moveTo = (index) => {
        const pagination = this._pagination;
        const sliderEl = this._wrapper.firstElementChild;
        const bulletSize = this._bulletSize;
        const wrapperSize = bulletSize * pagination.bulletCount;
        sliderEl.style.transform = `translate(${wrapperSize / 2 - (index + 0.5) * bulletSize}px)`;
        this._sliderIndex = index;
      };
    }
    destroy() {
      this._bullets = [];
      this._bulletSize = 0;
      this._previousIndex = -1;
      this._sliderIndex = -1;
    }
    render() {
      const wrapper = this._wrapper;
      const flicking2 = this._flicking;
      const pagination = this._pagination;
      const renderBullet = pagination.renderBullet;
      const anchorPoints = flicking2.camera.anchorPoints;
      const dynamicWrapperClass = `${pagination.classPrefix}-${PAGINATION.SCROLL_WRAPPER_SUFFIX}`;
      const bulletClass = `${pagination.classPrefix}-${PAGINATION.BULLET_SUFFIX}`;
      const sliderClass = `${pagination.classPrefix}-${PAGINATION.SCROLL_SLIDER_SUFFIX}`;
      const uninitClass = `${pagination.classPrefix}-${PAGINATION.SCROLL_UNINIT_SUFFIX}`;
      const sliderEl = document.createElement('div');
      addClass(sliderEl, sliderClass);
      addClass(wrapper, uninitClass);
      addClass(wrapper, dynamicWrapperClass);
      wrapper.appendChild(sliderEl);
      sliderEl.innerHTML = anchorPoints.map((_, index) => renderBullet(bulletClass, index)).join('\n');
      const bullets = [].slice.call(sliderEl.children);
      bullets.forEach((bullet, index) => {
        this._addBulletEvents(bullet, index);
      });
      if (bullets.length <= 0) return;
      const bulletStyle = getComputedStyle(bullets[0]);
      const bulletSize =
        bullets[0].clientWidth + parseFloat(bulletStyle.marginLeft) + parseFloat(bulletStyle.marginRight);
      wrapper.style.width = `${bulletSize * pagination.bulletCount}px`;
      this._bullets = bullets;
      this._bulletSize = bulletSize;
      this._previousIndex = -1;
      this.update(this._flicking.index);
      window.requestAnimationFrame(() => {
        removeClass(wrapper, uninitClass);
      });
    }
    update(index) {
      const pagination = this._pagination;
      const flicking$1 = this._flicking;
      const bullets = this._bullets;
      const prevIndex = this._previousIndex;
      const renderBullet = pagination.renderBullet;
      const renderActiveBullet = pagination.renderActiveBullet;
      const anchorPoints = flicking$1.camera.anchorPoints;
      const anchorOffset = anchorPoints[0].panel.index;
      const activeIndex = index - anchorOffset;
      if (anchorPoints.length <= 0) return;
      const bulletClass = `${pagination.classPrefix}-${PAGINATION.BULLET_SUFFIX}`;
      const bulletActiveClass = `${pagination.classPrefix}-${PAGINATION.BULLET_ACTIVE_SUFFIX}`;
      const prevClassPrefix = `${pagination.classPrefix}-${PAGINATION.SCROLL_PREV_SUFFIX}`;
      const nextClassPrefix = `${pagination.classPrefix}-${PAGINATION.SCROLL_NEXT_SUFFIX}`;
      const bulletPrevClass = (offset) => `${prevClassPrefix}${offset > 1 ? offset : ''}`;
      const bulletNextClass = (offset) => `${nextClassPrefix}${offset > 1 ? offset : ''}`;
      const prevClassRegex = new RegExp(`^${prevClassPrefix}`);
      const nextClassRegex = new RegExp(`^${nextClassPrefix}`);
      if (renderActiveBullet) {
        const prevBullet = bullets[prevIndex];
        if (prevBullet) {
          const newBullet = this._createBulletFromString(renderBullet(bulletClass, prevIndex), prevIndex);
          prevBullet.parentElement.replaceChild(newBullet, prevBullet);
          bullets[prevIndex] = newBullet;
        }
        const activeBullet = bullets[activeIndex];
        if (activeBullet) {
          const newActiveBullet = this._createBulletFromString(
            renderActiveBullet(bulletClass, activeIndex),
            activeIndex,
          );
          activeBullet.parentElement.replaceChild(newActiveBullet, activeBullet);
          bullets[activeIndex] = newActiveBullet;
        }
      }
      bullets.forEach((bullet, idx) => {
        const indexOffset = idx - activeIndex;
        const classList = bullet.className.split(' ');
        for (const className of classList) {
          if (className === bulletActiveClass || prevClassRegex.test(className) || nextClassRegex.test(className)) {
            removeClass(bullet, className);
          }
        }
        if (indexOffset === 0) {
          addClass(bullet, bulletActiveClass);
        } else if (indexOffset > 0) {
          addClass(bullet, bulletNextClass(Math.abs(indexOffset)));
        } else {
          addClass(bullet, bulletPrevClass(Math.abs(indexOffset)));
        }
      });
      pagination.scrollOnChange(activeIndex, {
        total: bullets.length,
        prevIndex,
        sliderIndex: this._sliderIndex,
        direction: activeIndex > prevIndex ? flicking.DIRECTION.NEXT : flicking.DIRECTION.PREV,
        bullets: [...bullets],
        moveTo: this.moveTo,
      });
      this._previousIndex = activeIndex;
    }
  }
  class Pagination {
    /**
     * @param options - Options for the Pagination instance
     * @example
     * ```ts
     * flicking.addPlugins(new Pagination({ type: "bullet", selector: ".flicking-pagination" }));
     * ```
     */
    constructor(options = {}) {
      this._flicking = null;
      this.update = () => {
        this._removeAllChilds();
        this._renderer.render();
      };
      this._onIndexChange = (evt) => {
        this._renderer.update(evt.index);
      };
      const {
        parentEl = null,
        selector = PAGINATION.SELECTOR,
        type = PAGINATION.TYPE.BULLET,
        classPrefix = PAGINATION.PREFIX,
        bulletCount = 5,
        renderBullet = (className) => `<span class="${className}"></span>`,
        renderActiveBullet = null,
        renderFraction = (currentClass, totalClass) =>
          `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`,
        fractionCurrentFormat = (index) => index.toString(),
        fractionTotalFormat = (index) => index.toString(),
        scrollOnChange = (index, ctx) => ctx.moveTo(index),
      } = options;
      this._parentEl = parentEl;
      this._selector = selector;
      this._type = type;
      this._classPrefix = classPrefix;
      this._bulletCount = bulletCount;
      this._renderBullet = renderBullet;
      this._renderActiveBullet = renderActiveBullet;
      this._renderFraction = renderFraction;
      this._fractionCurrentFormat = fractionCurrentFormat;
      this._fractionTotalFormat = fractionTotalFormat;
      this._scrollOnChange = scrollOnChange;
    }
    /** Current value of the {@link PaginationOptions.parentEl | parentEl} option. */
    get parentEl() {
      return this._parentEl;
    }
    /** Current value of the {@link PaginationOptions.selector | selector} option. */
    get selector() {
      return this._selector;
    }
    /** Current value of the {@link PaginationOptions.type | type} option. */
    get type() {
      return this._type;
    }
    /** Current value of the {@link PaginationOptions.classPrefix | classPrefix} option. */
    get classPrefix() {
      return this._classPrefix;
    }
    /** Current value of the {@link PaginationOptions.bulletCount | bulletCount} option. */
    get bulletCount() {
      return this._bulletCount;
    }
    /** Current value of the {@link PaginationOptions.renderBullet | renderBullet} option. */
    get renderBullet() {
      return this._renderBullet;
    }
    /** Current value of the {@link PaginationOptions.renderActiveBullet | renderActiveBullet} option. */
    get renderActiveBullet() {
      return this._renderActiveBullet;
    }
    /** Current value of the {@link PaginationOptions.renderFraction | renderFraction} option. */
    get renderFraction() {
      return this._renderFraction;
    }
    /** Current value of the {@link PaginationOptions.fractionCurrentFormat | fractionCurrentFormat} option. */
    get fractionCurrentFormat() {
      return this._fractionCurrentFormat;
    }
    /** Current value of the {@link PaginationOptions.fractionTotalFormat | fractionTotalFormat} option. */
    get fractionTotalFormat() {
      return this._fractionTotalFormat;
    }
    /** Current value of the {@link PaginationOptions.scrollOnChange | scrollOnChange} option. */
    get scrollOnChange() {
      return this._scrollOnChange;
    }
    /** Sets {@link PaginationOptions.parentEl | parentEl}. */
    set parentEl(val) {
      this._parentEl = val;
    }
    /** Sets {@link PaginationOptions.selector | selector}. */
    set selector(val) {
      this._selector = val;
    }
    /** Sets {@link PaginationOptions.type | type}. */
    set type(val) {
      this._type = val;
    }
    /** Sets {@link PaginationOptions.classPrefix | classPrefix}. */
    set bulletWrapperclassPrefixClass(val) {
      this._classPrefix = val;
    }
    /** Sets {@link PaginationOptions.bulletCount | bulletCount}. */
    set bulletCount(val) {
      this._bulletCount = val;
    }
    /** Sets {@link PaginationOptions.renderBullet | renderBullet}. */
    set renderBullet(val) {
      this._renderBullet = val;
    }
    /** Sets {@link PaginationOptions.renderActiveBullet | renderActiveBullet}. */
    set renderActiveBullet(val) {
      this._renderActiveBullet = val;
    }
    /** Sets {@link PaginationOptions.renderFraction | renderFraction}. */
    set renderFraction(val) {
      this._renderFraction = val;
    }
    /** Sets {@link PaginationOptions.fractionCurrentFormat | fractionCurrentFormat}. */
    set fractionCurrentFormat(val) {
      this._fractionCurrentFormat = val;
    }
    /** Sets {@link PaginationOptions.fractionTotalFormat | fractionTotalFormat}. */
    set fractionTotalFormat(val) {
      this._fractionTotalFormat = val;
    }
    /** Sets {@link PaginationOptions.scrollOnChange | scrollOnChange}. */
    set scrollOnChange(val) {
      this._scrollOnChange = val;
    }
    /** Initialize the plugin, create the pagination renderer, and attach event listeners to the Flicking instance.
     * @param flicking - The Flicking instance to attach this plugin to
     */
    init(flicking$1) {
      if (this._flicking) {
        this.destroy();
      }
      this._flicking = flicking$1;
      const type = this._type;
      const selector = this._selector;
      const parentEl = this._parentEl ? this._parentEl : flicking$1.element;
      const wrapper = parentEl.querySelector(selector);
      if (!wrapper) {
        throw new Error(`[Flicking-Pagination] Couldn't find element with the given selector: ${selector}`);
      }
      this._wrapper = wrapper;
      this._renderer = this._createRenderer(type);
      flicking$1.on(flicking.EVENTS.WILL_CHANGE, this._onIndexChange);
      flicking$1.on(flicking.EVENTS.WILL_RESTORE, this._onIndexChange);
      flicking$1.on(flicking.EVENTS.PANEL_CHANGE, this.update);
      this.update();
    }
    /** Destroy the plugin, remove all event listeners, and clean up pagination DOM elements. */
    destroy() {
      const flicking$1 = this._flicking;
      if (!flicking$1) {
        return;
      }
      flicking$1.off(flicking.EVENTS.WILL_CHANGE, this._onIndexChange);
      flicking$1.off(flicking.EVENTS.WILL_RESTORE, this._onIndexChange);
      flicking$1.off(flicking.EVENTS.PANEL_CHANGE, this.update);
      this._renderer.destroy();
      this._removeAllChilds();
      this._flicking = null;
    }
    _createRenderer(type) {
      const options = {
        flicking: this._flicking,
        pagination: this,
        wrapper: this._wrapper,
      };
      switch (type) {
        case PAGINATION.TYPE.BULLET:
          return new BulletRenderer(options);
        case PAGINATION.TYPE.FRACTION:
          return new FractionRenderer(options);
        case PAGINATION.TYPE.SCROLL:
          return new ScrollBulletRenderer(options);
        default:
          throw new Error(`[Flicking-Pagination] type "${type}" is not supported.`);
      }
    }
    _removeAllChilds() {
      const wrapper = this._wrapper;
      while (wrapper.firstChild) {
        wrapper.removeChild(wrapper.firstChild);
      }
    }
  }
  exports2.ARROW = ARROW;
  exports2.Arrow = Arrow;
  exports2.AutoPlay = AutoPlay;
  exports2.Fade = Fade;
  exports2.PAGINATION = PAGINATION;
  exports2.Pagination = Pagination;
  exports2.Parallax = Parallax;
  exports2.Perspective = Perspective;
  exports2.SYNC = SYNC;
  exports2.Sync = Sync;
  Object.defineProperty(exports2, Symbol.toStringTag, { value: 'Module' });
});
//# sourceMappingURL=plugins.js.map
